/**
 * 用户相关的React Hooks
 * 基于SWR提供用户认证相关的状态管理和API调用功能
 */

import useSWRMutation from 'swr/mutation'
import { useUserStore } from '@/stores/userStore'
import { API_PATHS, userService } from '../services/user.service'
import type {
  Account,
  CaptchaLoginRequest,
  LoginRequest,
  RegisterRequest,
} from '../types/user.types'

/**
 * 账号密码登录Hook
 * @returns SWR mutation对象，包含trigger（登录函数）、isMutating（加载状态）、error（错误信息）
 */
export const useLogin = () => {
  const { setUser } = useUserStore()

  return useSWRMutation<Account, Error, string, LoginRequest>(
    API_PATHS.LOGIN,
    (_, { arg }) => userService.login(arg),
    {
      onSuccess: (data) => {
        if (data) {
          setUser(data)
          // Token会由fetcher拦截器自动处理（如果已配置）
        }
      },
    }
  )
}

/**
 * 验证码登录Hook
 * @returns SWR mutation对象，包含trigger（登录函数）、isMutating（加载状态）、error（错误信息）
 */
export const useCaptchaLogin = () => {
  const { setUser } = useUserStore()

  return useSWRMutation<Account, Error, string, CaptchaLoginRequest>(
    API_PATHS.CAPTCHA_LOGIN,
    (_, { arg }) => userService.loginWithCaptcha(arg),
    {
      onSuccess: (data) => {
        if (data) {
          setUser(data)
          // Token会由fetcher拦截器自动处理（如果已配置）
        }
      },
    }
  )
}

/**
 * 用户注册Hook
 * @returns SWR mutation对象，包含trigger（注册函数）、isMutating（加载状态）、error（错误信息）
 */
export const useRegister = () => {
  const { setUser } = useUserStore()

  return useSWRMutation<Account, Error, string, RegisterRequest>(
    API_PATHS.REGISTER,
    (_, { arg }) => userService.register(arg),
    {
      onSuccess: (data) => {
        if (data) {
          setUser(data)
        }
      },
    }
  )
}

/**
 * 退出登录Hook
 * @returns SWR mutation对象，包含trigger（退出函数）、isMutating（加载状态）、error（错误信息）
 */
export const useLogout = () => {
  const { reset } = useUserStore()

  return useSWRMutation<null, Error, string, void>(
    API_PATHS.LOGOUT,
    () => userService.logout(),
    {
      onSuccess: () => {
        // 重置用户状态store
        reset()
        // 清除本地存储的认证信息
        localStorage.removeItem('token')
        localStorage.removeItem('user')
      },
    }
  )
}
